/* ============================================
   USE WORK THEME HOOK
   The Creative Orchestra - GEMINI Framework
   Hook for applying and managing work-specific themes
   ============================================ */

import { useEffect, useCallback, useMemo } from 'react';
import { useTheme } from '@context/ThemeContext';
import { workThemes, defaultTheme } from '@utils/workThemes';
import { WORKS_ORDER, GEMINI_STAGES } from '@utils/constants';

/**
 * Re-export useWorkTheme from context
 */
export { useWorkTheme } from '@context/ThemeContext';

/**
 * Hook for getting all work themes with metadata
 * Useful for rendering work orbs on landing page
 * 
 * @returns {Object} Work themes data and utilities
 */
export const useAllWorkThemes = () => {
  const { getOrbGradient, getWorkGlow } = useTheme();

  /**
   * Get all works with their themes in order
   */
  const worksWithThemes = useMemo(() => {
    return WORKS_ORDER.map(workId => ({
      id: workId,
      theme: workThemes[workId],
      orbGradient: getOrbGradient(workId),
      glow: getWorkGlow(workId, 'medium'),
      glowLarge: getWorkGlow(workId, 'large'),
    }));
  }, [getOrbGradient, getWorkGlow]);

  /**
   * Get works grouped by GEMINI stage
   */
  const worksByStage = useMemo(() => {
    const grouped = {};
    
    Object.entries(GEMINI_STAGES).forEach(([stageKey, stage]) => {
      grouped[stage.id] = {
        ...stage,
        works: stage.works.map(workId => ({
          id: workId,
          theme: workThemes[workId],
          orbGradient: getOrbGradient(workId),
          glow: getWorkGlow(workId, 'medium'),
        })),
      };
    });

    return grouped;
  }, [getOrbGradient, getWorkGlow]);

  /**
   * Get stage info for a work
   * @param {string} workId - Work identifier
   * @returns {Object|null} Stage information
   */
  const getStageForWork = useCallback((workId) => {
    const theme = workThemes[workId];
    if (!theme) return null;

    const stageEntry = Object.entries(GEMINI_STAGES).find(
      ([_, stage]) => stage.works.includes(workId)
    );

    return stageEntry ? stageEntry[1] : null;
  }, []);

  /**
   * Get adjacent works (for navigation)
   * @param {string} workId - Current work ID
   * @returns {Object} Previous and next work IDs
   */
  const getAdjacentWorks = useCallback((workId) => {
    const currentIndex = WORKS_ORDER.indexOf(workId);
    
    return {
      prev: currentIndex > 0 ? WORKS_ORDER[currentIndex - 1] : null,
      next: currentIndex < WORKS_ORDER.length - 1 ? WORKS_ORDER[currentIndex + 1] : null,
    };
  }, []);

  return {
    worksWithThemes,
    worksByStage,
    getStageForWork,
    getAdjacentWorks,
    totalWorks: WORKS_ORDER.length,
    workOrder: WORKS_ORDER,
  };
};

/**
 * Hook for applying theme on route change
 * Automatically sets theme based on current work page
 * 
 * @param {string} workId - Current work ID (from route params)
 * @returns {Object} Current theme and utilities
 */
export const useRouteTheme = (workId) => {
  const { setWorkTheme, resetTheme, activeTheme, isTransitioning } = useTheme();

  /**
   * Apply theme when workId changes
   */
  useEffect(() => {
    if (workId && workThemes[workId]) {
      setWorkTheme(workId);
    }

    // Reset theme on unmount
    return () => {
      resetTheme();
    };
  }, [workId, setWorkTheme, resetTheme]);

  /**
   * Get current work theme (or default)
   */
  const currentTheme = useMemo(() => {
    return workId && workThemes[workId] ? workThemes[workId] : defaultTheme;
  }, [workId]);

  return {
    theme: currentTheme,
    isTransitioning,
    isWorkTheme: workId && workThemes[workId],
  };
};

/**
 * Hook for generating themed component styles
 * Creates inline styles based on current or specified theme
 * 
 * @param {string} workId - Optional work ID (uses active theme if not provided)
 * @returns {Object} Style generator functions
 */
export const useThemedStyles = (workId = null) => {
  const { activeTheme, getThemedStyles } = useTheme();

  /**
   * Get theme to use (specified or active)
   */
  const theme = useMemo(() => {
    if (workId && workThemes[workId]) {
      return workThemes[workId];
    }
    return activeTheme;
  }, [workId, activeTheme]);

  /**
   * Generate button styles
   * @param {string} variant - Button variant ('solid', 'outline', 'ghost')
   * @returns {Object} CSS styles
   */
  const buttonStyles = useCallback((variant = 'solid') => {
    const { colors } = theme;

    switch (variant) {
      case 'solid':
        return {
          backgroundColor: colors.accent,
          color: '#ffffff',
          border: 'none',
        };
      case 'outline':
        return {
          backgroundColor: 'transparent',
          color: colors.accent,
          border: `2px solid ${colors.accent}`,
        };
      case 'ghost':
        return {
          backgroundColor: 'transparent',
          color: colors.accent,
          border: 'none',
        };
      default:
        return {};
    }
  }, [theme]);

  /**
   * Generate card styles
   * @param {boolean} elevated - Add elevation/shadow
   * @returns {Object} CSS styles
   */
  const cardStyles = useCallback((elevated = false) => {
    const { colors, glows } = theme;
    
    return {
      backgroundColor: `${colors.primary}10`,
      borderColor: `${colors.accent}20`,
      ...(elevated && { boxShadow: glows.small }),
    };
  }, [theme]);

  /**
   * Generate text styles
   * @param {string} variant - Text variant ('accent', 'muted', 'gradient')
   * @returns {Object} CSS styles
   */
  const textStyles = useCallback((variant = 'accent') => {
    const { colors, gradients } = theme;

    switch (variant) {
      case 'accent':
        return { color: colors.accent };
      case 'muted':
        return { color: colors.textMuted };
      case 'gradient':
        return {
          background: gradients.text,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
        };
      default:
        return {};
    }
  }, [theme]);

  /**
   * Generate glow styles
   * @param {string} size - Glow size ('small', 'medium', 'large')
   * @returns {Object} CSS styles
   */
  const glowStyles = useCallback((size = 'medium') => {
    const { glows } = theme;
    return { boxShadow: glows[size] || glows.medium };
  }, [theme]);

  /**
   * Generate background styles
   * @param {boolean} dark - Use dark variant
   * @returns {Object} CSS styles
   */
  const backgroundStyles = useCallback((dark = true) => {
    const { gradients } = theme;
    return {
      background: dark ? gradients.backgroundDark : gradients.background,
    };
  }, [theme]);

  /**
   * Generate orb styles
   * @returns {Object} CSS styles for orb element
   */
  const orbStyles = useCallback(() => {
    const { gradients, glows } = theme;
    return {
      background: gradients.orb,
      boxShadow: glows.medium,
    };
  }, [theme]);

  /**
   * Get CSS variables object for inline styles
   * @returns {Object} CSS variables
   */
  const cssVariables = useMemo(() => {
    return theme.cssVariables || {};
  }, [theme]);

  return {
    theme,
    buttonStyles,
    cardStyles,
    textStyles,
    glowStyles,
    backgroundStyles,
    orbStyles,
    cssVariables,
    
    // Direct access to theme properties
    colors: theme.colors,
    gradients: theme.gradients,
    glows: theme.glows,
  };
};

/**
 * Hook for orb-specific styling on landing page
 * 
 * @param {string} workId - Work identifier
 * @param {boolean} isHovered - Whether orb is hovered
 * @returns {Object} Orb styles and animation config
 */
export const useOrbStyles = (workId, isHovered = false) => {
  const theme = workThemes[workId] || defaultTheme;

  /**
   * Get orb container styles
   */
  const containerStyles = useMemo(() => ({
    background: theme.gradients.orb,
    boxShadow: isHovered ? theme.glows.large : theme.glows.medium,
    transform: isHovered ? 'scale(1.15)' : 'scale(1)',
    transition: 'all 0.3s ease-out',
  }), [theme, isHovered]);

  /**
   * Get orb label styles
   */
  const labelStyles = useMemo(() => ({
    color: isHovered ? theme.colors.accent : 'rgba(255, 255, 255, 0.9)',
    textShadow: isHovered ? theme.glows.text : 'none',
    transition: 'all 0.3s ease-out',
  }), [theme, isHovered]);

  /**
   * Get ring animation styles (for hover effect)
   */
  const ringStyles = useMemo(() => ({
    borderColor: theme.colors.accent,
    opacity: isHovered ? 0.6 : 0,
    transform: isHovered ? 'scale(1.3)' : 'scale(1)',
    transition: 'all 0.4s ease-out',
  }), [theme, isHovered]);

  return {
    containerStyles,
    labelStyles,
    ringStyles,
    theme,
    accentColor: theme.colors.accent,
  };
};

export default useRouteTheme;
